import { StyledTable } from "../../styles/StyledUnit/StyledContent";
import { PropTypes } from "prop-types";
import DeleteIcon from "../../assets/img/icons/delete.svg";
import CloseIcon from "../../assets/img/icons/close.svg";
import SaveIcon from "../../assets/img/icons/save.svg";
import EditIcon from "../../assets/img/icons/edit-black.svg";
import { getDecisionQuestions, updateDecisionQuestions } from "../../api/decision/questions";
import { useState } from "react";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import OrderSelect from "../OrderSelect/OrderSelect";
import { useEffect } from "react";
import { StyledDecisionTableRow } from "../../styles/StyledUnit/StyledDecisionTable";
import { StyledQuestionInput } from "../../styles/StyledUnit/StyledDecisionTable";

export function DecisionQuestionItem({ 
	id, 
	refetch = false, 
	data, 
	edit = false, 
	setAddNew = false, 
	openModal = false, 
	setDeletedId = false, 
	index, 
	total, 
	onReorder, 
	editingId = null, 
	setEditingId = () => {},
	newDecision = false
}) {
	const { axiosPrivate } = useAxiosPrivate();
	const [isEdit, setIsEdit] = useState(edit);
	const [questionText, setQuestionText] = useState(data.question || "");
	const [order, setOrder] = useState(typeof index === "number" ? index + 1 : 1);

	useEffect(() => {
		if (isEdit) {
			setQuestionText(data.question || "");
			setOrder(index + 1);
		}
	}, [isEdit, data.question, index]);

	const handleChange = (e) => {
		setQuestionText(e.target.value);
	};
	const isLocked = editingId !== null && editingId !== data.id;

	const handleEdit = async () => {
		try {
			if (newDecision) {
				// For new decisions, handle locally
				const tempQuestions = JSON.parse(sessionStorage.getItem("tempDecisionQuestions") || "[]");
				const newOrderIndex = order - 1;
				
				if (!data.id) {
					// Adding new question
					const newQuestion = { 
						id: Date.now(), 
						question: questionText 
					};
					tempQuestions.splice(newOrderIndex, 0, newQuestion);
				} else {
					// Editing existing question
					const questionIndex = tempQuestions.findIndex(q => q.id === data.id);
					if (questionIndex !== -1) {
						tempQuestions[questionIndex].question = questionText;
						// Reorder if needed
						if (newOrderIndex !== questionIndex) {
							const [moved] = tempQuestions.splice(questionIndex, 1);
							tempQuestions.splice(newOrderIndex, 0, moved);
						}
					}
				}
				
				sessionStorage.setItem("tempDecisionQuestions", JSON.stringify(tempQuestions));
				
				setIsEdit(false);
				setEditingId(null);
				if (edit && setAddNew) setAddNew(false);
				
				// Force a re-render by calling refetch if it exists
				if (refetch && typeof refetch === "function") refetch();
				return;
			}

			const newOrderIndex = order - 1;

			const currentQuestions = await getDecisionQuestions(axiosPrivate, id);
			if (!Array.isArray(currentQuestions)) throw new Error("Failed to load current questions");

			const cleaned = currentQuestions.map((q) => ({ question: q.question }));
			if (!data.id) {
				cleaned.splice(newOrderIndex, 0, { question: questionText });
			} else {
				cleaned[index].question = questionText;
				if (newOrderIndex !== index) {
					const [moved] = cleaned.splice(index, 1);
					cleaned.splice(newOrderIndex, 0, moved);
				}
			}

			await updateDecisionQuestions(axiosPrivate, refetch, id, cleaned);
			await refetch();

			setIsEdit(false);
			setEditingId(null);
			if (edit && setAddNew) setAddNew(false);
		} catch (error) {
			console.error("Error updating questions", error);
		}
	};


	return (
		<StyledDecisionTableRow>
			{isEdit ? (
				<OrderSelect value={order} total={total} onChange={setOrder} />
			) : (
				<span>{typeof index === "number" ? index + 1 : 1}</span>
			)}
			{isEdit ? (
				<StyledQuestionInput type="text" name="question" value={questionText} onChange={handleChange} />
			) : (
				<span>{data.question}</span>
			)}
			<div>
				{isEdit ? (
					<>
						<img onClick={handleEdit} src={SaveIcon} alt="Save" role="img" />
						<img
							onClick={() => {
								setIsEdit(false);
								setEditingId(null);
								if (setAddNew) setAddNew(false);
							}}
							src={CloseIcon}
							alt="Cancel"
							role="img"
						/>
					</>
				) : (
					<>
						<img
							onClick={() => {
							if (!isLocked) {
								setIsEdit(true);
								setEditingId(data.id);
							}
							}}
							src={EditIcon}
							alt="Edit"
							role="img"
							style={{ opacity: isLocked ? 0.3 : 1, pointerEvents: isLocked ? "none" : "auto" }}
						/>
						<img
							onClick={() => {
							if (!isLocked && openModal) {
								setDeletedId(data.id);
								openModal();
							}
							}}
							src={DeleteIcon}
							alt="Delete"
							role="img"
							style={{ opacity: isLocked ? 0.3 : 1, pointerEvents: isLocked ? "none" : "auto" }}
						/>
					</>
				)}
			</div>
		</StyledDecisionTableRow>
	);
}

DecisionQuestionItem.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	data: PropTypes.object,
	edit: PropTypes.bool,
	index: PropTypes.number,
	total: PropTypes.number,
	onReorder: PropTypes.func,
	editingId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  	setEditingId: PropTypes.func,
	setAddNew: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	refetch: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	openModal: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	setDeletedId: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
	newDecision: PropTypes.bool,
};
